import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useAuth } from '../context/AuthContext';
import { signUpSchema, SignUpFormData } from '../utils/validationSchemas';
import '../styles/auth.css';

export default function SignUpAuth() {
  const navigate = useNavigate();
  const { signUp, signInWithOAuth } = useAuth();
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<SignUpFormData>({
    resolver: zodResolver(signUpSchema),
  });

  const onSubmit = async (data: SignUpFormData) => {
    try {
      setError(null);
      setSuccessMessage(null);
      setLoading(true);

      await signUp(data.email, data.password, {
        name: data.name,
        company: data.company,
      });

      setSuccessMessage('Account created! Please check your email to verify your account.');
      setTimeout(() => {
        navigate('/signin');
      }, 3000);
    } catch (err: any) {
      setError(err.message || 'An error occurred during sign up');
    } finally {
      setLoading(false);
    }
  };

  const handleOAuth = async (provider: 'google' | 'github' | 'discord' | 'microsoft') => {
    try {
      setError(null);
      await signInWithOAuth(provider);
    } catch (err: any) {
      setError(err.message || `An error occurred signing in with ${provider}`);
    }
  };

  return (
    <section className="auth-section">
      <div className="container">
        <div className="auth-content">
          <div className="auth-header">
            <h1 className="auth-title text-gradient">
              Create an account
            </h1>
          </div>

          {error && (
            <div style={{
              padding: '12px 16px',
              marginBottom: '16px',
              backgroundColor: '#fee',
              border: '1px solid #fcc',
              borderRadius: '6px',
              color: '#c33',
              fontSize: '14px',
            }}>
              {error}
            </div>
          )}

          {successMessage && (
            <div style={{
              padding: '12px 16px',
              marginBottom: '16px',
              backgroundColor: '#efe',
              border: '1px solid #cfc',
              borderRadius: '6px',
              color: '#3c3',
              fontSize: '14px',
            }}>
              {successMessage}
            </div>
          )}

          <form className="auth-form" onSubmit={handleSubmit(onSubmit)}>
            <div className="auth-form-fields">
              <div>
                <label htmlFor="name" className="auth-label">
                  Name <span className="text-red">*</span>
                </label>
                <input
                  id="name"
                  type="text"
                  className="form-input"
                  placeholder="Your full name"
                  {...register('name')}
                />
                {errors.name && (
                  <span style={{ color: '#c33', fontSize: '12px', marginTop: '4px', display: 'block' }}>
                    {errors.name.message}
                  </span>
                )}
              </div>

              <div>
                <label htmlFor="company" className="auth-label">
                  Company Name <span className="text-red">*</span>
                </label>
                <input
                  id="company"
                  type="text"
                  className="form-input"
                  placeholder="Your company name"
                  {...register('company')}
                />
                {errors.company && (
                  <span style={{ color: '#c33', fontSize: '12px', marginTop: '4px', display: 'block' }}>
                    {errors.company.message}
                  </span>
                )}
              </div>

              <div>
                <label htmlFor="email" className="auth-label">
                  Work Email <span className="text-red">*</span>
                </label>
                <input
                  id="email"
                  type="email"
                  className="form-input"
                  placeholder="Your work email"
                  {...register('email')}
                />
                {errors.email && (
                  <span style={{ color: '#c33', fontSize: '12px', marginTop: '4px', display: 'block' }}>
                    {errors.email.message}
                  </span>
                )}
              </div>

              <div>
                <label htmlFor="password" className="auth-label">
                  Password <span className="text-red">*</span>
                </label>
                <input
                  id="password"
                  type="password"
                  className="form-input"
                  placeholder="Password (at least 10 characters)"
                  {...register('password')}
                />
                {errors.password && (
                  <span style={{ color: '#c33', fontSize: '12px', marginTop: '4px', display: 'block' }}>
                    {errors.password.message}
                  </span>
                )}
              </div>

              <div>
                <label htmlFor="confirmPassword" className="auth-label">
                  Confirm Password <span className="text-red">*</span>
                </label>
                <input
                  id="confirmPassword"
                  type="password"
                  className="form-input"
                  placeholder="Confirm your password"
                  {...register('confirmPassword')}
                />
                {errors.confirmPassword && (
                  <span style={{ color: '#c33', fontSize: '12px', marginTop: '4px', display: 'block' }}>
                    {errors.confirmPassword.message}
                  </span>
                )}
              </div>
            </div>

            <div className="auth-form-actions">
              <button type="submit" className="btn btn-primary w-full" disabled={loading}>
                {loading ? 'Creating account...' : 'Register'}
              </button>
              <div className="divider">or</div>

              <button
                type="button"
                className="btn btn-secondary w-full"
                onClick={() => handleOAuth('google')}
                disabled={loading}
              >
                Sign Up with Google
              </button>
              <button
                type="button"
                className="btn btn-secondary w-full"
                onClick={() => handleOAuth('github')}
                disabled={loading}
              >
                Sign Up with GitHub
              </button>
              <button
                type="button"
                className="btn btn-secondary w-full"
                onClick={() => handleOAuth('discord')}
                disabled={loading}
              >
                Sign Up with Discord
              </button>
              <button
                type="button"
                className="btn btn-secondary w-full"
                onClick={() => handleOAuth('microsoft')}
                disabled={loading}
              >
                Sign Up with Microsoft
              </button>
            </div>
          </form>

          <div className="auth-footer">
            Already have an account?{' '}
            <Link to="/signin" className="link">
              Sign in
            </Link>
          </div>
        </div>
      </div>
    </section>
  );
}
